import { format, parse } from 'date-fns';

import { SingleFoodComponentWithBatches } from '../interfaces';
import { CookingStationName, CupSizes, CupsQuantity, FoodComponent, MeasurementUnit } from '../types';
import { ComponentService } from './componentService';

export const getCookedRawFactor = (foodComponent: FoodComponent) =>
  (foodComponent.weight ?? 0) / (foodComponent.cookedWeight ?? 1);

export const weightCalculator = (
  foodComponent: FoodComponent | SingleFoodComponentWithBatches,
  weight: number,
  count: number,
  cupsQuantity?: CupsQuantity[],
  quantities?: number[],
  cookedRawFactor = 1
) => {
  if (foodComponent.measurementUnit === MeasurementUnit.cup && foodComponent.cups && cupsQuantity) {
    return cupsCalculatorUsingCupsQuantity(cupsQuantity, foodComponent.cups) * cookedRawFactor;
  } else if (foodComponent.measurementUnit === MeasurementUnit.cup && foodComponent.cups && quantities) {
    const totalWeight = ComponentService.getWeightFromAllCupsQuantities(
      quantities.map((q) => ({ component: q })),
      foodComponent.cups
    );
    return Math.round(totalWeight * cookedRawFactor);
  }

  return Math.round(weight * count);
};

export const cupsCalculatorUsingCupsQuantity = (cupsQuantity: CupsQuantity[], cups: CupSizes[]) => {
  let result = 0;
  for (const cupQuantity of cupsQuantity) {
    result += ComponentService.calculateComponentWeightFromCups(cupQuantity.cup, cups) * cupQuantity.quantity;
  }

  return result;
};

export const getComponentsWithNonZeroCount = <T extends { count?: number }>(foodComponents: T[]): T[] =>
  foodComponents.filter((c) => (c.count ?? 0) > 0);

export const getActualCookedWeightStation = (
  foodComponent: Pick<FoodComponent, 'cookingStations'>
): CookingStationName | undefined => {
  const cookingStations = foodComponent.cookingStations ?? [];
  if (!cookingStations.length) return undefined;

  let lastStation = cookingStations[cookingStations.length - 1]?.name;
  if (lastStation === CookingStationName.portioning && cookingStations.length > 1) {
    lastStation = cookingStations[cookingStations.length - 2]?.name;
  }

  return lastStation;
};

export const hasActualCookedWeightEntered = (foodComponent: Pick<FoodComponent, 'actualCookedWeight'>): boolean =>
  (foodComponent.actualCookedWeight ?? []).some((entry) => Number(entry?.quantity) > 0);

export const getAvailableHoursChanges = (foodComponents: FoodComponent[]) => {
  const hourlyData = foodComponents[0]?.currentCookedWeight?.hourlyData;

  return hourlyData
    ? hourlyData.map((data) => {
        const parsedDate = parse(data.timestamp, 'dd-MM-yyyy-HH:mm', new Date());
        return format(parsedDate, 'dd-MMM-yyyy h:mm a');
      })
    : [];
};
